import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BrIconComponent } from '../../shared/ui/icon/icon.component';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, merge, Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ApiKeysService, ApiKey, ApiKeyPage, ApiProvider, ServiceMeta } from '../../services/api-keys.service';
import { ApiKeyEditorComponent } from './api-key-editor.component';
import { ApiKeyCreateDialogComponent } from './apikey-create-dialog.component';
import { ConfirmationDialogService } from '../../shared/ui/confirmation-dialog/confirmation-dialog.service';

/** Credential list for one service; owns its requests, paging and recovery independently. */
@Component({
  selector: 'app-api-key-list',
  standalone: true,
  imports: [CommonModule, BrIconComponent, MatPaginatorModule, ApiKeyEditorComponent],
  templateUrl: './api-key-list.component.html',
  styleUrls: ['../configuration-list.css', './api-key-list.component.css'],
})
export class ApiKeyListComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) service!: ServiceMeta;
  @Input() inlineCreate = false;
  page: ApiKeyPage = { items: [], total: 0, page: 1, pageSize: 25 };
  loading = false;
  error = '';
  private request?: Subscription;
  /** Guards a single remove workflow (its confirmation and the DELETE) against re-entry. */
  private removing = false;
  private readonly destroyed$ = new Subject<void>();

  constructor(public store: ApiKeysService, private dialog: MatDialog,
    public auth: AuthService, private snackBar: MatSnackBar,
    private confirmation: ConfirmationDialogService) {
    // A session change abandons any in-flight removal, so the guard must release with it —
    // otherwise a removal captured under the old account would leave the new one unable to
    // delete anything. The old workflow's own callbacks never touch the guard once the
    // session they belong to is gone (see `owns()` in remove()).
    this.auth.tenant?.sessionChanged$?.pipe(takeUntil(this.destroyed$))
      .subscribe(() => { this.removing = false; });
  }

  ngOnChanges(): void {
    this.page = { items: [], total: 0, page: 1, pageSize: 25 };
    this.load();
  }
  ngOnDestroy(): void { this.destroyed$.next(); this.destroyed$.complete(); }

  meta(c: ApiKey): ApiProvider | undefined { return this.store.providerMeta(c.provider); }
  trackById(_: number, c: ApiKey): number { return c.id; }
  canManage(): boolean { return this.auth.hasPermission('ApiKey.Manage'); }

  onPage(event: PageEvent): void { this.load(event.pageIndex + 1, event.pageSize); }

  setPrimary(c: ApiKey): void {
    if (c.primary || !this.canManage()) return;
    this.store.setPrimary(c.id).pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => this.load(),
      error: () => this.snackBar.open('Could not change the company default. Please retry.', 'OK', { duration: 4000 }),
    });
  }

  add(): void {
    const ref = this.dialog.open(ApiKeyCreateDialogComponent, {
      width: '460px', maxWidth: '92vw', data: { service: this.service.id },
    });
    ref.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(r => {
      if (!r?.saved) return;
      const current = this.page;
      const lastPage = Math.max(
        1,
        Math.ceil((current.total + 1) / current.pageSize));
      this.load(lastPage, current.pageSize);
    });
  }

  edit(c: ApiKey): void {
    if (!this.canManage()) return;
    const ref = this.dialog.open(ApiKeyCreateDialogComponent, {
      width: '460px', maxWidth: '92vw', data: { credential: c },
    });
    ref.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(r => {
      if (r?.saved) this.load();
    });
  }

  remove(c: ApiKey): void {
    if (!this.canManage()) return;
    // One removal at a time: a repeat click, or a click while the dialog is still open, must not
    // stack another confirmation or fire a second DELETE before the first has resolved.
    if (this.removing) return;
    // Pin the session this removal belongs to. If the account/session changes while the dialog is
    // open or the DELETE is in flight, this removal is abandoned: its confirmation and completion
    // must neither act on the new account nor unlock a removal the new account has since started —
    // even when the replacement session happens to reuse the same bearer token. sessionVersion is
    // authoritative; `owns()` re-reads it so a stale callback can recognise it no longer applies.
    const version = this.auth.tenant?.sessionVersion ?? 0;
    const owns = () => (this.auth.tenant?.sessionVersion ?? 0) === version;
    // A session change or this list's destruction cancels the workflow: the shared service closes
    // the open dialog, and the DELETE is unsubscribed in flight.
    const cancel$ = merge(this.destroyed$, this.auth.tenant?.sessionChanged$ ?? EMPTY);
    // A reference row borrows another company key's secret, so removing it only drops this service's
    // use of the shared key; a source-key removal still cascades to every configuration using it.
    const message = c.sourceKeyId != null
      ? 'This stops this service from using the shared key. The key itself and other services that use it keep working. This cannot be undone.'
      : 'Any service configurations that use this key are removed with it. This cannot be undone.';
    this.removing = true;
    this.confirmation.confirm({
      title: `Remove this ${this.meta(c)?.label ?? c.provider} key?`,
      message, confirmLabel: 'Remove', danger: true,
    }, cancel$).pipe(takeUntil(cancel$)).subscribe(confirmed => {
      if (!owns()) return;
      if (!confirmed) { this.removing = false; return; }
      this.store.remove(c.id).pipe(takeUntil(cancel$)).subscribe({
        next: () => { if (!owns()) return; this.removing = false; this.load(); },
        // A refusal used to land nowhere: the row stayed, nothing was said, and
        // the only reading available to the user was that the button is broken.
        error: (e: { error?: { error?: string } }) => {
          if (!owns()) return;
          this.removing = false;
          this.snackBar.open(e?.error?.error ?? 'Could not remove the key.', 'OK', { duration: 4000 });
        },
      });
    });
  }

  /** Refresh this service only; a newer page request cancels the previous one. */
  load(page = this.page.page, pageSize = this.page.pageSize): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.page = { ...this.page, page, pageSize };
    this.request = this.store.list(this.service.id, page, pageSize).pipe(takeUntil(this.destroyed$)).subscribe({
      next: result => {
        if (!result.items.length && result.total > 0 && page > 1) {
          this.load(Math.max(1, Math.ceil(result.total / pageSize)), pageSize);
          return;
        }
        this.page = result;
        this.loading = false;
      },
      error: () => { this.loading = false; this.error = 'Could not load API keys. Please retry.'; },
    });
  }
}
